import api from './api';
import type { RecentTabEntry } from 'kci-design-lib';
import { TRANSLATIONS } from '../i18n/translations';
import { environment } from '../environments/environment';
import { getAccessToken, msalInitPromise, msalInstance } from '../auth/msalConfig';
import { getUserChatSessions as fetchUserChatSessions, type UserChatSessionDTO } from './chatHistoryApi';

const AGENT_DASHBOARD_LABEL = TRANSLATIONS.en['dash.agents.title'];

// ---- Configuration ---- //

const TAB_API_BASE = environment.apiBaseUrl.replace(/\/$/, '');

const TAB_API_PATH = '/User/Tab';
const CHAT_HISTORY_AGGREGATOR_API_PATH = '/ChatHistoryAggregator';
const ACTIVITY_DASHBOARD_API_PATH = '/ActivityDashboard';
const AGENT_API_PATH = '/Agent';

const tabStateCacheByPanelId = new Map<string, UserTabStatePayload>();

function resolveClaraChatApiBaseUrl(): string {
  const configuredBase = (import.meta.env.VITE_pubSubApiBaseUrl as string | undefined)?.trim();
  return (configuredBase || environment.apiBaseUrl).replace(/\/+$/, '');
}

// ---- Backend Shape Types ---- //

export interface TabPin {
  Id: string;
  UserId: string;
  CreatedOn: string;
}

export interface BackendTabContext {
  Kind: 'chat' | 'workspace' | 'agent' | 'engagement';
  SessionId?: string | null;
  AgentId?: string | null;
  AgentName?: string | null;
  IsGlobal?: boolean | null;
  WorkspaceId?: string | null;
  ObjectiveId?: string | null;
  ObjectiveType?: string | null;
  ObjectiveCategory?: string | null;
  EngagementId?: string | null;
  ClientId?: string | null;
  PageId?: string | null;
  ContentType?: string | null;
  StorageKey?: string | null;
}

export interface BackendRecentTab {
  TabId: string;
  Title: string;
  Type: string;
  LastAccessedDate: string;
  IsClosed: boolean;
  ClosedAt: string | null;
  AgentId: string;
  AgentName: string;
  IsGlobal?: boolean | null;
  WorkspaceName: string;
  SessionId: string;
  WorkspaceObjectiveId: string;
  ContextVersion?: number | null;
  Context?: BackendTabContext | null;
}

export interface RecentTabIdentifiers {
  tabId: string;
  sessionId: string | null;
  agentId: string | null;
  tabType: string;
}

export interface TabPanelResponse {
  Id: string;
  Tabs: BackendRecentTab[];
  ActiveTab: string;
  Pins: TabPin[];
  Context: string;
}

interface UserTabStatePayload {
  Id: string;
  Tabs: BackendRecentTab[];
  ActiveTab: string;
}

export type RecentTabPopoverActionId =
  | 'pin-toggle'
  | 'share'
  | 'edit'
  | 'rename'
  | 'download'
  | 'delete';

export interface RecentTabPopoverAction {
  id: RecentTabPopoverActionId;
  label: string;
  prefixInstance: string;
}

export interface GraphUserLookupResult {
  id: string;
  displayName: string;
  email: string;
}

type RecentTabPopoverActionInput = Pick<
  RecentTabEntry,
  'label' | 'isPinned'
> & {
  // New fields used to identify/authorize agent actions.
  id?: string;
  agentId?: string | null;
  isGlobal?: boolean | null;

  tabKind?: RecentTabEntry['tabKind'];
  isOwnedByCurrentUser?: boolean;
  isCustomAgent?: boolean;
  isPublishedCustomAgent?: boolean;
};

// ---- Tab Type Mapping ---- //

type TabKind = RecentTabEntry['tabKind'];

type KnownTabKind = Exclude<TabKind, null>;

const TAB_TYPE_MAP: Partial<Record<string, KnownTabKind>> = {
  Chat: 'chats',
  Workspace: 'workspaces',
  Agent: 'agents',
  Engagement: 'engagements',
};

/**
 * Fetch the current user's chat sessions.
 * Wraps chatHistoryApi.getUserChatSessions so tab/title-sync logic can
 * depend on tabManagementService instead of reaching into chatHistoryApi.
 */
export async function getUserSessions(
  limit = 100,
  offset = 0,
): Promise<UserChatSessionDTO[]> {
  return fetchUserChatSessions(limit, offset);
}

/**
 * Returns the actions shown in the recent-tab kebab popover for a given entry.
 * This keeps tab-kind -> actions behavior in one place instead of UI-level conditionals.
 */
export function getRecentTabPopoverActions(
  entry: RecentTabPopoverActionInput,
): RecentTabPopoverAction[] {
  const actions: RecentTabPopoverAction[] = [
    {
      id: 'pin-toggle',
      label: entry.isPinned ? 'Unpin' : 'Pin',
      prefixInstance: 'Push Pin',
    },
  ];

  const isAllAgentsActivity = entry.label === AGENT_DASHBOARD_LABEL;
  if (isAllAgentsActivity) {
    return actions;
  }

  const isAgentEntry = entry.tabKind === 'agents'
    || entry.id?.startsWith('agent:') === true
    || !!entry.agentId;
  if (isAgentEntry) {
    if (entry.isGlobal === true || entry.isOwnedByCurrentUser !== true) {
      return actions;
    }

    actions.push(
      { id: 'share', label: 'Share', prefixInstance: 'Share' },
      { id: 'edit', label: 'Edit', prefixInstance: 'Edit' },
      { id: 'delete', label: 'Delete', prefixInstance: 'Delete' },
    );
    return actions;
  }

  if (entry.tabKind === 'chats') {
    actions.push(
      { id: 'rename', label: 'Rename', prefixInstance: 'Edit' },
      { id: 'delete', label: 'Delete', prefixInstance: 'Delete' },
      { id: 'download', label: 'Download', prefixInstance: 'Download' },
    );
  }
  return actions;
}

function normalizeId(value: string | null | undefined): string | null {
  const normalized = (value ?? '').trim();

  return normalized.length > 0 ? normalized : null;
}

export function buildRecentTabIdentifierMap(
  tabs: BackendRecentTab[],
): Map<string, RecentTabIdentifiers> {
  return new Map(
    tabs.map((tab) => [
      tab.TabId,
      {
        tabId: tab.TabId,
        sessionId: normalizeId(tab.SessionId),
        agentId: normalizeId(tab.AgentId),
        tabType: tab.Type,
      },
    ]),
  );
}

// ---- Context Builder ---- //

interface ContextEntry {
  key: string;
  value: string;
}

export function buildTabContext(): ContextEntry[] {
  return [
    { key: 'ctry', value: 'US' },
    { key: 'SCREEN.TYPE', value: 'Standalone' },
    {
      key: 'Environment',
      value:
        (import.meta.env.VITE_ENVIRONMENT as string | undefined)?.trim() ||
        'local',
    },
  ];
}

// ---- API Functions ---- //

/**
 * Creates or retrieves the user's tab panel.
 * Returns the panel Id and current Pins array which are needed for subsequent calls.
 */
export async function initTabPanel(): Promise<TabPanelResponse> {
  const panel = await api.fetch<TabPanelResponse>(
    `${TAB_API_BASE}${TAB_API_PATH}`,
    {
      method: 'POST',
    },
  );

  tabStateCacheByPanelId.set(panel.Id, {
    Id: panel.Id,
    Tabs: [...panel.Tabs],
    ActiveTab: panel.ActiveTab,
  });

  return panel;
}

/**
 * Fetches the recent-tab history for a given panel.
 *
 * @param panelId The tab panel Id returned by initTabPanel.
 * @param page Zero-based page index (default 0).
 * @param offset Number of items to return (default 10).
 */
export async function fetchRecentTabs(
  panelId: string,
  page = 0,
  offset = 10,
): Promise<BackendRecentTab[]> {
  void panelId;

  return api.fetch<BackendRecentTab[]>(
    `${TAB_API_BASE}${TAB_API_PATH}/RecentTab`,
    {
      method: 'POST',
      body: JSON.stringify({
        Context: buildTabContext(),
        Page: page,
        Offset: offset,
      }),
    },
  );
}

// ---- Save / Update Tab ---- //

export interface SaveTabPayload {
  TabId: string;
  Title: string;
  Type: string;
  SessionId?: string;
  WorkspaceName?: string;
  WorkspaceObjectiveId?: string;
  AgentId?: string;

  // New optional fields.
  AgentName?: string;
  IsGlobal?: boolean | null;

  IsClosed?: boolean;
  ContextVersion?: number | null;
  Context?: BackendTabContext | null;
}

interface SaveTabOptions {
  activeTabId?: string;
}

async function getOrFetchTabState(
  panelId: string,
): Promise<UserTabStatePayload> {
  const cached = tabStateCacheByPanelId.get(panelId);

  if (cached) {
    return cached;
  }

  try {
    const panel = await api.fetch<TabPanelResponse>(
      `${TAB_API_BASE}${TAB_API_PATH}`,
      { method: 'POST' },
    );

    const fetchedState: UserTabStatePayload = {
      Id: panel.Id,
      Tabs: [...panel.Tabs],
      ActiveTab: panel.ActiveTab,
    };

    tabStateCacheByPanelId.set(panelId, fetchedState);

    return fetchedState;
  } catch {
   
     if (cached) {
        return cached;
      }

    const emptyState: UserTabStatePayload = {
      Id: panelId,
      Tabs: [],
      ActiveTab: '',
    };

    tabStateCacheByPanelId.set(panelId, emptyState);

    return emptyState;
  }
}

function ensureNonEmpty(
  value: string,
  field: string,
): string {
  const normalized = value.trim();

  if (!normalized) {
    throw new Error(`${field} is required.`);
  }

  return normalized;
}

function toBackendTabRecord(
  payload: SaveTabPayload,
  existing?: BackendRecentTab,
): BackendRecentTab {
  const nowIso = new Date().toISOString();

  const isClosed =
    payload.IsClosed ??
    existing?.IsClosed ??
    false;

  return {
    TabId: payload.TabId,
    Title: payload.Title,
    Type: payload.Type,
    LastAccessedDate: nowIso,

    IsClosed: isClosed,

    ClosedAt: isClosed
      ? existing?.ClosedAt ?? nowIso
      : null,

    /**
     * New behavior:
     * Use values supplied by the caller first, otherwise preserve
     * values already stored in the cached tab.
     */
    AgentId:
      payload.AgentId ??
      existing?.AgentId ??
      '',

    AgentName:
      payload.AgentName ??
      existing?.AgentName ??
      '',

    IsGlobal:
      payload.IsGlobal ??
      existing?.IsGlobal ??
      null,

    WorkspaceName:
      payload.WorkspaceName ??
      existing?.WorkspaceName ??
      '',

    SessionId:
      payload.SessionId ??
      existing?.SessionId ??
      '',

    WorkspaceObjectiveId:
      payload.WorkspaceObjectiveId ??
      existing?.WorkspaceObjectiveId ??
      '',

    ContextVersion:
      payload.ContextVersion ??
      existing?.ContextVersion ??
      null,

    Context:
      payload.Context ??
      existing?.Context ??
      null,
  };
}

/**
 * Sends the complete tab state to the backend.
 *
 * Important:
 * The cache is updated only after the PUT succeeds.
 * This prevents the local cache from containing state that the server
 * rejected.
 */
async function patchUserTabState(
  panelId: string,
  nextState: UserTabStatePayload,
): Promise<TabPanelResponse> {
  try {
    const response = await api.fetch<TabPanelResponse>(
      `${TAB_API_BASE}${TAB_API_PATH}/${encodeURIComponent(panelId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(nextState),
      },
    );

    /**
     * Update the local cache from the state we successfully persisted.
     *
     * Important: We intentionally use nextState rather than response here.
     * Some backend implementations return only the affected tab(s) in the
     * PUT response instead of the full Tabs array. Trusting response.Tabs
     * would corrupt the local cache to contain only the newly-saved tab,
     * causing every subsequent saveTab call to overwrite all other tabs.
     *
     * nextState reflects the full intended state we sent, so it is the
     * correct source of truth for the local cache.
     */
    tabStateCacheByPanelId.set(panelId, {
      Id: response.Id ?? panelId,
      Tabs: [...nextState.Tabs],
      ActiveTab: response.ActiveTab ?? nextState.ActiveTab,
    });

    return response;
  } catch (error) {
    console.error(
      '[KCI-RecentTabs] failed to save user tab state',
      {
        panelId,
        tabCount: nextState.Tabs.length,
        activeTab: nextState.ActiveTab,
        tabs: nextState.Tabs.map((tab) => ({
          tabId: tab.TabId,
          title: tab.Title,
          type: tab.Type,
          sessionId: tab.SessionId,
          agentId: tab.AgentId,
          workspaceObjectiveId: tab.WorkspaceObjectiveId,
        })),
        error,
      },
    );

    throw error;
  }
}

/**
 * Save or update a tab entry in the backend.
 *
 * Used when:
 * - a new tab is created
 * - a session/objective is associated with a tab
 * - a tab title changes
 * - a tab is closed
 * - agent information changes
 */
export async function saveTab(
  panelId: string,
  payload: SaveTabPayload,
  options: SaveTabOptions = {},
): Promise<TabPanelResponse> {
  const normalizedPanelId = ensureNonEmpty(
    panelId,
    'panelId',
  );

  const normalizedTabId = ensureNonEmpty(
    payload.TabId,
    'TabId',
  );

  const currentState = await getOrFetchTabState(
    normalizedPanelId,
  );

  const existing = currentState.Tabs.find(
    (tab) => tab.TabId === normalizedTabId,
  );

  const nextRecord = toBackendTabRecord(
    {
      ...payload,
      TabId: normalizedTabId,
    },
    existing,
  );

  const nextTabs = existing
    ? currentState.Tabs.map((tab) =>
        tab.TabId === normalizedTabId
          ? nextRecord
          : tab,
      )
    : [
        ...currentState.Tabs,
        nextRecord,
      ];

  const nextState: UserTabStatePayload = {
    Id: normalizedPanelId,
    Tabs: nextTabs,

    /**
     * Preserve the old behavior:
     * saveTab makes the saved/new tab the active tab unless
     * an explicit activeTabId was supplied.
     */
    ActiveTab:
      options.activeTabId ??
      nextRecord.TabId,
  };

  /**
   * Do NOT update the cache here.
   *
   * patchUserTabState updates it only after the PUT succeeds.
   */
  return patchUserTabState(
    normalizedPanelId,
    nextState,
  );
}

/**
 * Save the complete set of tabs.
 */
export async function saveTabs(
  panelId: string,
  tabs: SaveTabPayload[],
  activeTabId = '',
): Promise<TabPanelResponse> {
  const normalizedPanelId = ensureNonEmpty(
    panelId,
    'panelId',
  );

  const currentState = await getOrFetchTabState(
    normalizedPanelId,
  );

  /**
   * Build an update map from the supplied tabs so we can do O(1) lookups.
   */
  const updateMap = new Map<string, BackendRecentTab>();

  for (const tab of tabs) {
    const normalizedTabId = ensureNonEmpty(tab.TabId, 'TabId');

    const existing = currentState.Tabs.find(
      (cachedTab) => cachedTab.TabId === normalizedTabId,
    );

    const record = toBackendTabRecord(
      { ...tab, TabId: normalizedTabId },
      existing,
    );

    updateMap.set(normalizedTabId, record);
  }

  /**
   * Merge strategy: start from the full cached backend tab list so that
   * tabs not present in the current UI (e.g. tabs from a previous session,
   * or tabs on other devices) are preserved as-is. Only tabs included in
   * the `tabs` argument are updated. New tabs (not yet in the cache) are
   * appended at the end.
   *
   * This prevents saveTabs from wiping backend history when the caller
   * only passes the currently visible open tabs (e.g. after a close).
   */
  const mergedTabs: BackendRecentTab[] = currentState.Tabs.map((cachedTab) =>
    updateMap.has(cachedTab.TabId)
      ? updateMap.get(cachedTab.TabId)!
      : cachedTab,
  );

  // Append any brand-new tabs that were not already in the cache.
  for (const [tabId, record] of updateMap) {
    if (!currentState.Tabs.some((t) => t.TabId === tabId)) {
      mergedTabs.push(record);
    }
  }

  /**
   * Preserve the explicitly supplied active tab when possible.
   *
   * If it is not present in the merged set, fall back to the previously
   * active tab if it still exists in the merged set.
   *
   * Finally, use the first available tab.
   *
   * This prevents saveTabs from accidentally clearing the active tab
   * when the caller passes an empty/invalid activeTabId.
   */
  const activeTab =
    activeTabId &&
    mergedTabs.some((tab) => tab.TabId === activeTabId)
      ? activeTabId
      : currentState.ActiveTab &&
          mergedTabs.some((tab) => tab.TabId === currentState.ActiveTab)
        ? currentState.ActiveTab
        : mergedTabs[0]?.TabId ?? '';

  const nextState: UserTabStatePayload = {
    Id: normalizedPanelId,
    Tabs: mergedTabs,
    ActiveTab: activeTab,
  };

  /**
   * Do not update the cache before PUT succeeds.
   */
  return patchUserTabState(
    normalizedPanelId,
    nextState,
  );
}

// ---- Tab Close / Pin Operations ---- //

export async function removeTab(
  panelId: string,
  tabId: string,
  identifiers?: { sessionId?: string | null; agentId?: string | null },
): Promise<TabPanelResponse> {
  const normalizedPanelId = ensureNonEmpty(panelId, 'panelId');
  const normalizedTabId = ensureNonEmpty(tabId, 'tabId');
  const currentState = await getOrFetchTabState(normalizedPanelId);
  const sessionId = normalizeId(identifiers?.sessionId);
  const agentId = normalizeId(identifiers?.agentId);
  const nextTabs = currentState.Tabs.filter((tab) =>
    tab.TabId !== normalizedTabId
    && (!sessionId || normalizeId(tab.SessionId) !== sessionId)
    && (!agentId || normalizeId(tab.AgentId) !== agentId),
  );
  const activeTab = nextTabs.some((tab) => tab.TabId === currentState.ActiveTab)
    ? currentState.ActiveTab
    : nextTabs[0]?.TabId ?? '';

  return patchUserTabState(normalizedPanelId, {
    Id: normalizedPanelId,
    Tabs: nextTabs,
    ActiveTab: activeTab,
  });
}

/**
 * Mark a tab as closed in the backend by setting IsClosed: true.
 */
export async function markTabClosed(
  panelId: string,
  tabId: string,
  title: string,
  type: string,
): Promise<TabPanelResponse> {
  return saveTab(
    panelId,
    {
      TabId: tabId,
      Title: title,
      Type: type,
      IsClosed: true,
    },
  );
}

/**
 * Pins a tab.
 * Pin.Id equals TabId on this API.
 */
export async function pinTab(
  panelId: string,
  tabId: string,
): Promise<TabPanelResponse> {
  return api.fetch<TabPanelResponse>(
    `${TAB_API_BASE}${TAB_API_PATH}/Pin/${encodeURIComponent(panelId)}/${encodeURIComponent(tabId)}`,
    {
      method: 'POST',
    },
  );
}

/**
 * Unpins a tab.
 * Uses tabId as the pin identifier.
 */
export async function unpinTab(
  panelId: string,
  tabId: string,
): Promise<TabPanelResponse> {
  return api.fetch<TabPanelResponse>(
    `${TAB_API_BASE}${TAB_API_PATH}/UnPin/${encodeURIComponent(panelId)}/${encodeURIComponent(tabId)}`,
    {
      method: 'DELETE',
    },
  );
}

// ---- CRUD Operations (Recent Tabs Context Actions) ---- //

/**
 * Rename a chat or workspace session.
 */
export async function renameSession(
  sessionId: string,
  title: string,
): Promise<unknown> {
  return api.fetch(
    `${TAB_API_BASE}${CHAT_HISTORY_AGGREGATOR_API_PATH}/Rename`,
    {
      method: 'PUT',
      body: JSON.stringify({
        SessionId: sessionId,
        Name: title,
      }),
    },
  );
}

/**
 * Delete a chat or workspace session.
 */
export async function deleteSession(
  sessionId: string,
): Promise<unknown> {
  return api.fetch(
    `${TAB_API_BASE}${CHAT_HISTORY_AGGREGATOR_API_PATH}/ChatHistory/${encodeURIComponent(sessionId)}`,
    {
      method: 'DELETE',
    },
  );
}

/**
 * Download a single chat session as XLSX.
 */
export async function downloadChat(
  sessionId: string,
): Promise<Blob> {
  return api.fetchBlob(
    `${TAB_API_BASE}${CHAT_HISTORY_AGGREGATOR_API_PATH}/DownloadChat`,
    {
      method: 'POST',
      body: JSON.stringify({
        SessionId: sessionId,
        Type: 'sessionchathistory',
      }),
    },
  );
}

/**
 * Get agent details for editing.
 */
export async function getAgentDetails(
  dataSourceId: string,
): Promise<unknown> {
  return api.fetch(
    `${resolveClaraChatApiBaseUrl()}${AGENT_API_PATH}/Edit/${encodeURIComponent(dataSourceId)}`,
    {
      method: 'GET',
    },
  );
}

/**
 * Get users who already have an active share for an agent.
 */
export async function getAgentSharedUserIds(dataSourceId: string): Promise<string[]> {
  const response = await getAgentDetails(dataSourceId);
  let details = response;

  if (typeof details === 'string') {
    try {
      details = JSON.parse(details) as unknown;
    } catch {
      return [];
    }
  }

  if (!details || typeof details !== 'object') {
    return [];
  }

  const sharedUsers = (details as { SharedUserIdWithStatus?: unknown }).SharedUserIdWithStatus;
  if (!Array.isArray(sharedUsers)) {
    return [];
  }

  return sharedUsers.flatMap((sharedUser) => {
    if (!sharedUser || typeof sharedUser !== 'object') {
      return [];
    }

    const entry = sharedUser as { UserID?: unknown; Status?: unknown };
    const userId = typeof entry.UserID === 'string' ? entry.UserID.trim().toLowerCase() : '';
    const status = typeof entry.Status === 'string' ? entry.Status.trim().toLowerCase() : '';
    return userId && status !== 'rejected' ? [userId] : [];
  });
}

/**
 * Update agent details.
 */
export async function updateAgent(
  agentData: unknown,
): Promise<unknown> {
  return api.fetch(
    `${TAB_API_BASE}${AGENT_API_PATH}`,
    {
      method: 'PUT',
      body: JSON.stringify(agentData),
    },
  );
}

/**
 * Delete an agent.
 */
export async function deleteAgent(dataSourceId: string): Promise<void> {
  const deleted = await api.fetch<boolean>(
    `${TAB_API_BASE}${AGENT_API_PATH}/Delete/${encodeURIComponent(dataSourceId)}`,
    {
      method: 'DELETE',
    },
  );
  if (!deleted) {
    throw new Error('Agent deletion was not completed.');
  }
}

export async function deleteAgentRuns(dataSourceId: string): Promise<unknown> {
  return api.fetch(
    `${TAB_API_BASE}${ACTIVITY_DASHBOARD_API_PATH}/AgentRun/Agent/${encodeURIComponent(dataSourceId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Share an agent with other users.
 */
export async function shareAgent(
  agentId: string,
  userIds: string[],
): Promise<unknown> {
  const normalizedUserIds = userIds
    .map((value) => value.trim())
    .filter(
      (value) => value.length > 0,
    );

  const token = await getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (environment.aiIntegrationId) {
    headers.aiintegrationid = environment.aiIntegrationId;
  }

  const response = await fetch(`${resolveClaraChatApiBaseUrl()}${AGENT_API_PATH}/Share`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      AgentId: agentId,
      UserIds: normalizedUserIds,
    }),
  });

  if (!response.ok) {
    throw new Error(`Agent share failed: ${response.status} ${response.statusText}`);
  }

  return response.status === 204 ? undefined : response.json();
}

// ---- Graph User Lookup ---- //

interface GraphSearchUsersApiResponse {
  value?: GraphSearchUser[];
}

interface GraphSearchUser {
  id?: string;
  displayName?: string;
  mail?: string | null;
  userPrincipalName?: string | null;
}

/**
 * Lookup users directly from Microsoft Graph, matching Clara AI's share picker.
 */
export async function lookupUserEmails(
  searchQuery: string,
  top = 10,
): Promise<GraphUserLookupResult[]> {
  const query = searchQuery.trim();

  if (!query) {
    return [];
  }

  await msalInitPromise;
  const account = msalInstance.getActiveAccount() ?? (msalInstance.getAllAccounts() ?? [])[0];
  if (!account) {
    return [];
  }

  const tokenResponse = await msalInstance.acquireTokenSilent({
    account,
    scopes: ['User.Read.All'],
  });
  const graphUrl = new URL('https://graph.microsoft.com/v1.0/users');
  graphUrl.search = new URLSearchParams({
    '$search': `"displayName:${query}" OR "mail:${query}"`,
    '$select': 'id,displayName,mail,userPrincipalName',
    '$top': String(top),
  }).toString();
  const response = await fetch(
    graphUrl.toString(),
    {
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        ConsistencyLevel: 'eventual',
      },
    },
  );

  if (!response.ok) {
    return [];
  }

  const graphResponse = await response.json() as GraphSearchUsersApiResponse;
  const users = graphResponse.value ?? [];

  return users
    .map((user) => {
      const email = (
        user.mail ??
        user.userPrincipalName ??
        ''
      ).trim();

      if (!email) {
        return null;
      }

      return {
        id: (user.id ?? email).trim(),
        displayName: (
          user.displayName ??
          email
        ).trim(),
        email,
      };
    })
    .filter(
      (
        user,
      ): user is GraphUserLookupResult =>
        user !== null,
    );
}

// ---- Mapping ---- //

/**
 * Maps backend recent tabs to local RecentTabEntry values.
 *
 * Important compatibility rules:
 *
 * 1. Existing Type-based mapping is retained.
 * 2. Tabs with AgentId are treated as agent tabs, which supports
 *    the new agent-conversation behavior.
 * 3. Existing pin behavior is unchanged.
 * 4. New agent metadata is exposed to the UI.
 */
export function mapBackendTabsToRecentEntries(
  tabs: BackendRecentTab[],
  pins: TabPin[],
): RecentTabEntry[] {
  const pinnedMap = new Map(
    pins.map((pin) => [
      pin.Id,
      pin,
    ]),
  );

  return tabs.map(
    (tab): RecentTabEntry => {
      const pin = pinnedMap.get(
        tab.TabId,
      );

      /**
       * Keep the original Type mapping as the baseline.
       *
       * If AgentId exists, the new behavior identifies this as an
       * agent-related tab. This supports agent conversations even if
       * the backend Type is not "Agent".
       */
      const tabKind = tab.AgentId
        ? 'agents'
        : TAB_TYPE_MAP[tab.Type] ?? null;

      const isAgentTab = !!tab.AgentId;
      const separatorIndex = tab.TabId.indexOf('::');
      const workspaceGroupId =
        tabKind === 'workspaces' && separatorIndex > 0
          ? tab.TabId.slice(0, separatorIndex).trim() || null
          : null;

      return {
        id: tab.TabId,

        label: tab.Title.trim() || 'Untitled',
        clientLabel:
          tab.WorkspaceName ||
          undefined,
          
        groupId: workspaceGroupId,
        engId: tabKind === 'workspaces' ? tab.TabId : workspaceGroupId,

        timestamp:
          tab.LastAccessedDate
            ? Date.parse(
                tab.LastAccessedDate,
              )
            : Date.now(),

        tabKind,

        isPinned: !!pin,

        pinnedAt: pin
          ? Date.parse(
              pin.CreatedOn,
            )
          : null,

        /**
         * Preserve undefined for non-agent tabs, just like the old
         * implementation did.
         *
         * For agent tabs, the new backend IsGlobal value determines
         * ownership.
         */
        isOwnedByCurrentUser:
          isAgentTab
            ? tab.IsGlobal === false
            : undefined,

        isCustomAgent:
          isAgentTab
            ? tab.IsGlobal === false
            : undefined,

        isPublishedCustomAgent:
          undefined,

        // New agent-related fields.
        agentId:
          normalizeId(
            tab.AgentId,
          ),

        isGlobal:
          tab.IsGlobal,

        isAgentConversation:
          isAgentTab,

        memberFirmCode: null,
      };
    },
  );
}
